import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/layout/PageHeader';
import LocalizedLink from '@/components/shared/LocalizedLink';
import { methodologiesApi } from '@/utils/api-pb';
import type { CatalogMethod, MethodCategory } from '@/types/methodologies';

// Fixed order so the grouping is stable across loads; `research` is excluded —
// it belongs to F-03's `/research` tabs, not the general catalog.
const CATEGORY_ORDER: MethodCategory[] = ['strategy', 'prioritization', 'creative', 'legal', 'ideas'];

export default function MethodsCatalogPage() {
  const { t } = useTranslation('methodologies');
  const [catalog, setCatalog] = useState<CatalogMethod[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const [catalogResult, artifactsResult] = await Promise.allSettled([
          methodologiesApi.catalog(),
          methodologiesApi.list(),
        ]);
        if (catalogResult.status === 'fulfilled') setCatalog(catalogResult.value);
        else console.error('Error loading methodology catalog:', catalogResult.reason);

        if (artifactsResult.status === 'fulfilled') {
          const byMethod: Record<string, number> = {};
          for (const artifact of artifactsResult.value) {
            byMethod[artifact.method] = (byMethod[artifact.method] || 0) + 1;
          }
          setCounts(byMethod);
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const visibleMethods = catalog.filter((m) => m.category !== 'research');
  const categories = CATEGORY_ORDER.filter((category) =>
    visibleMethods.some((m) => m.category === category),
  );

  if (isLoading) {
    return (
      <>
        <PageHeader title={t('catalog.title')} description={t('catalog.description')} />
        <div className="flex items-center justify-center py-[80px]">
          <div className="w-[32px] h-[32px] border-2 border-[#2197f1] border-t-transparent rounded-full animate-spin" />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title={t('catalog.title')} description={t('catalog.description')} />

      {categories.length === 0 ? (
        <p className="font-segoe text-[16px] leading-[24px] text-[#565858]">{t('catalog.empty')}</p>
      ) : (
        <div className="flex flex-col gap-[32px]">
          {categories.map((category) => (
            <div key={category} className="flex flex-col gap-[16px]">
              <h2 className="font-manrope font-bold text-[20px] leading-[28px] text-[#0d0d0d]">
                {t(`catalog.categories.${category}`)}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[16px]">
                {visibleMethods
                  .filter((m) => m.category === category)
                  .map((method) => (
                    <LocalizedLink
                      key={method.id}
                      to={`/methods/${method.id}`}
                      className="bg-[#f4f4f3] hover:bg-[#eeeeee] rounded-[16px] p-[24px] flex flex-col gap-[12px] transition-colors"
                    >
                      <h3 className="font-manrope font-bold text-[18px] leading-[24px] text-[#0d0d0d]">
                        {t(`methods.${method.id}.title`)}
                      </h3>
                      <p className="font-segoe text-[14px] leading-[20px] text-[#565858] flex-1">
                        {t(`methods.${method.id}.why`)}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="font-segoe text-[13px] leading-[18px] text-[#969696]">
                          {t('catalog.card.countCreated', { count: counts[method.id] || 0 })}
                        </span>
                        <span className="font-segoe font-semibold text-[14px] leading-[20px] text-[#2197f1]">
                          {t('catalog.card.openButton')}
                        </span>
                      </div>
                    </LocalizedLink>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
